import { useState, useRef } from 'react';

import { ISelection, ISelectionHostsReturnedData } from './interaces';

export const useSelectionHosts = (
  isEdit: boolean,
  selectedItems: Set<string>,
  setSelectedItems: React.Dispatch<React.SetStateAction<Set<string>>>
): ISelectionHostsReturnedData => {
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionBox, setSelectionBox] = useState<ISelection>({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });

  const [startPoint, setStartPoint] = useState({ x: 0, y: 0 });
  const [currentPoint, setCurrentPoint] = useState({ x: 0, y: 0 });

  const [isClick, setIsClick] = useState<boolean>(false);
  const initialSelectedRef = useRef<Set<string>>(new Set());
  const scrollInterval = useRef<number | null>(null);
  const lastScrollTop = useRef<number | null>(null);

  const localRef = useRef<HTMLDivElement>(null);

  const clearScrollInterval = (): void => {
    if (scrollInterval.current !== null) {
      window.clearInterval(scrollInterval.current);
      scrollInterval.current = null;
    }
  };

  const handleMouseDown = (e: globalThis.MouseEvent): void => {
    if (isEdit) return;
    if (e.button === 2) return;

    const root = document.getElementById('root');
    if (!root) return;

    const rootRect = root.getBoundingClientRect();

    const x = e.clientX - rootRect.left;
    const y = e.clientY - rootRect.top;

    setIsClick(true);
    initialSelectedRef.current = new Set(selectedItems);

    setStartPoint({ x, y });
    setCurrentPoint({ x, y });

    lastScrollTop.current = localRef.current?.scrollTop ?? null;

    setIsSelecting(true);
  };

  const handleMouseMove = (e: globalThis.MouseEvent): void => {
    if (!isSelecting) return;

    setIsClick(false);

    const root = document.getElementById('root');
    if (!root) return;

    const rootRect = root.getBoundingClientRect();

    const x = e.clientX - rootRect.left;
    const y = e.clientY - rootRect.top;

    setCurrentPoint({ x, y });
    autoScroll(e);

    const box: ISelection = {
      x: Math.min(startPoint.x, x),
      y: Math.min(startPoint.y, y),
      width: Math.abs(x - startPoint.x),
      height: Math.abs(y - startPoint.y),
    };

    setSelectionBox(box);

    updateSelection(e, box);
  };

  const updateSelection = (e: MouseEvent, box: ISelection): void => {
    const items = document.querySelectorAll('[data-host-id]');

    const next = new Set(initialSelectedRef.current);

    items.forEach((el) => {
      const id = el.getAttribute('data-host-id');
      if (!id) return;

      const rect = el.getBoundingClientRect();
      const rootRect = document.getElementById('root')?.getBoundingClientRect();

      const left = rect.left - (rootRect?.left || 0);
      const right = rect.right - (rootRect?.left || 0);
      const top = rect.top - (rootRect?.top || 0);
      const bottom = rect.bottom - (rootRect?.top || 0);

      const fullyInside =
        left >= box.x &&
        right <= box.x + box.width &&
        top >= box.y &&
        bottom <= box.y + box.height;

      if (fullyInside) {
        if (e.ctrlKey) {
          if (initialSelectedRef.current.has(id)) next.delete(id);
          else next.add(id);
        } else {
          next.add(id);
        }
      } else if (!e.ctrlKey) {
        next.delete(id);
      }
    });
    setSelectedItems(next);
  };

  const autoScroll = (e: MouseEvent): void => {
    const container = localRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();

    const y = e.clientY;

    const threshold = 80;
    const step = 20;

    if (y < rect.top + threshold) {
      if (scrollInterval.current == null) {
        scrollInterval.current = window.setInterval(() => {
          const prev = container.scrollTop;
          const next = Math.max(0, prev - step);
          if (next === prev) return;

          container.scrollTop = next;

          const delta = next - prev;
          setStartPoint((s) => ({ x: s.x, y: s.y - delta }));
          lastScrollTop.current = next;
        }, 16);
      }
      return;
    }

    if (y > rect.bottom - threshold) {
      if (scrollInterval.current == null) {
        scrollInterval.current = window.setInterval(() => {
          const prev = container.scrollTop;
          const max = container.scrollHeight - container.clientHeight;
          const next = Math.min(max, prev + step);
          if (next === prev) return;

          container.scrollTop = next;

          const delta = next - prev;
          setStartPoint((s) => ({ x: s.x, y: s.y - delta }));
          lastScrollTop.current = next;
        }, 16);
      }
      return;
    }

    clearScrollInterval();
  };

  const handleMouseUp = (e: globalThis.MouseEvent): void => {
    clearScrollInterval();

    const isQuickMenu = (e.target as HTMLElement).closest(
      '[data-name="quick-menu"]'
    );
    const isSidebar = (e.target as HTMLElement).closest(
      '[data-name="sidebar"]'
    );

    if (isClick && !e.ctrlKey && e.button !== 2 && !isQuickMenu && !isSidebar) {
      setSelectedItems(new Set());
    }

    setIsSelecting(false);
    setSelectionBox({ x: 0, y: 0, width: 0, height: 0 });

    window.removeEventListener('mouseup', handleMouseUp);
  };

  const handleClick = (e: globalThis.MouseEvent): void | Set<string> => {
    const item = (e.target as HTMLElement).closest('[data-host-id]');
    if (!item) return;

    const id = item.getAttribute('data-host-id');
    if (!id) return;

    if (e.ctrlKey) {
      setSelectedItems((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    } else {
      setSelectedItems(new Set([id]));
    }
  };

  const selectionStyle = {
    left: Math.min(startPoint.x, currentPoint.x),
    top: Math.min(startPoint.y, currentPoint.y),
    width: Math.abs(currentPoint.x - startPoint.x),
    height: Math.abs(currentPoint.y - startPoint.y),
  };

  return {
    localRef,
    isSelecting,
    selectionBox,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleClick,
    selectionStyle,
  };
};
